import React, { useCallback, useState, useMemo, useRef, useId } from "react";
import "../../../scss/manage-users.scss";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ManageUserRecord from "./widgets/manageUserRecord.widget";
import ManagePendingInviteRecord from "./widgets/managePendingInviteRecord.widget";
import { RapButton } from "@dynata/rex-raptor";
import UserInvitePopup from "./widgets/userInvite.widget";
import "react-toastify/dist/ReactToastify.css";
import { useGetOrganization } from "./hooks";
import useListUsersAndPendingInvites from "hooks/user/useListUsersAndPendingInvites";
import useListAccounts from "hooks/account/useListAccounts";
import { useStateContext } from "state";

// Choices for organization level admin - This is only used for organizations with multiple accounts.
// Contributor: is_organization_admin = false
// Organization Owner: is_organization_admin = true
export const organizationLevelRoles = ["Contributor", "Organization Owner"];

// Role choices for organizations with multiple accounts.
export const multiAccountRoles = ["Inactive", "Standard", "Admin"];

// Role choices for organizations with only a single account.
export const singleAccountRoles = ["Standard", "Admin", "Organization Owner"];

const ManageUsers: React.FC = () => {
  const navigate = useNavigate();
  const state = useStateContext();

  // NEW STUFF
  const users = useListUsersAndPendingInvites();
  // Investigate if at this point this is not undefined
  const accounts = useListAccounts(state.user?.organizationId!);

  const { data: getOrganizationData } = useGetOrganization();
  const popupRef = useRef<HTMLDialogElement>(null);
  const [orguserPopupState, setOrgUserPopupState] = useState<boolean>(false);

  const sortedUsers = useMemo(() => {
    const u = users.data?.users || [];
    return u.sort((a, b) => {
      if (a.id === state.user?.id) return -1;
      if (b.id === state.user?.id) return 1;
      return 0;
    });
  }, [users.data, state.user]);

  const onCloseCreateUserPopup = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.close();
    }
  }, []);

  const onOpenCreateUserPopup = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.showModal();
    }
  }, []);

  return (
    <div
      className={
        orguserPopupState || popupRef.current?.open
          ? "manageUsers_wrapper popup-open"
          : "manageUsers_wrapper"
      }
    >
      {accounts.isSuccess && accounts.data !== undefined && (
        <UserInvitePopup
          ref={popupRef}
          onClose={onCloseCreateUserPopup}
          organizationAccountsData={accounts.data}
          organizationData={getOrganizationData!}
        />
      )}

      <div className="manageUsers_nav pb-2">
        <div className="manageUser_left_container">
          <div className="manageUsers_navItems">
            <FaChevronLeft
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/dashboard")}
            />
          </div>
          <div className="manageUsers_navItems">
            <span className="pageHeading">Manage Users</span>
          </div>
        </div>
      </div>

      <div className="manageUsers_header_wrapper">
        <div className="mu_heading">Users</div>

        <RapButton onClick={onOpenCreateUserPopup}>Invite User</RapButton>
      </div>

      <div className="manageUser_table_wrapper">
        <table className="manageUsers_table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>{accounts.data?.length! > 1 ? "Account Roles" : "Roles"}</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {accounts.isSuccess &&
              accounts.data !== undefined &&
              sortedUsers.map((user) => (
                <ManageUserRecord
                  key={`user-${user.cognitoUsername}`}
                  user={user}
                  organizationAccounts={accounts.data}
                  setOrgUserPopupState={setOrgUserPopupState}
                  organizationData={getOrganizationData!}
                  isMe={user.id === state.user?.id}
                />
              ))}
          </tbody>
        </table>
      </div>
      {users.data !== undefined &&
        users.data.pendingInvites.length > 0 &&
        accounts.data !== undefined && (
          <>
            <div className="manageUsers_header_wrapper">
              <div className="mu_heading">Pending Invites</div>
            </div>

            <div className="manageUser_table_wrapper">
              <table className="manageUsers_table">
                <thead>
                  <th>Name</th>
                  <th>Email</th>
                  <th>
                    {accounts.data?.length! > 1 ? "Account Roles" : "Roles"}
                  </th>
                  <th>Actions</th>
                </thead>
                <tbody>
                  {users.data.pendingInvites.map((user) => (
                    <ManagePendingInviteRecord
                      key={`user-${user.id}`}
                      user={user}
                      organizationAccounts={accounts.data}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
    </div>
  );
};

export default ManageUsers;
